import React from "react";
import { Row, Col, Card, Placeholder } from "react-bootstrap";
import { FaArrowUp, FaArrowDown, FaMinus } from "react-icons/fa";

/**
 * KPI cards for the usage pages.
 *
 * Deliberately mirrors AzureCostInsights so the two Product Team pages read as one
 * product: same 4-up md={3} grid, same left-border accent, same uppercase label / h4
 * value / small trend line, same arrow indicator.
 */

/**
 * Above this, a percentage stops being information. Growth from a near-zero baseline
 * produces values like 731,381.8%, which tells you nothing the arrow does not already
 * say and stretches whatever column it lands in.
 */
const MAX_DISPLAY_PERCENT = 999;

/**
 * Arrow + percentage, matching the Azure Costs indicator.
 *
 * Rising usage is coloured as a concern rather than a success: growth toward a tier
 * limit is a commercial signal, the same way rising spend is on the cost page.
 *
 * Very large magnitudes are capped for display only — the exact value stays in the
 * title attribute and in the Excel export, so nothing is lost, it is just not shouted.
 */
export function TrendIndicator({ value, suffix }) {
  if (value === null || value === undefined) {
    return <span className="text-muted">N/A</span>;
  }

  const magnitude = Math.abs(value);
  const isFlat = magnitude < 0.5;
  const isUp = value > 0;
  const color = isFlat ? "text-muted" : isUp ? "text-danger" : "text-success";
  const Icon = isFlat ? FaMinus : isUp ? FaArrowUp : FaArrowDown;

  const capped = magnitude > MAX_DISPLAY_PERCENT;
  const text = capped ? `>${MAX_DISPLAY_PERCENT}%` : `${magnitude.toFixed(1)}%`;

  return (
    <span
      className={color}
      title={capped ? `${value.toFixed(1)}% — growth from a very small prior month` : undefined}
    >
      <Icon className="me-1" size={12} />
      {text}
      {suffix ? ` ${suffix}` : ""}
    </span>
  );
}

// Neutral accent for cards that are just reporting a number — the same fixed teal used
// for non-connector steps on the workflow canvas (see connectorColor.js). Color is spent
// only where a card is actually evaluative (see `tone` below); everything else stays on
// this one accent so four unrelated metrics don't read as four different categories.
const NEUTRAL_ACCENT = "#007681";

/**
 * @param {Array} cards  [{ label, value, icon, tone, trend, trendSuffix, caption }]
 *   `tone` is optional and only for a card whose value is itself good/bad news (e.g. a
 *   count of clients over their limit) — pass a Bootstrap variant ("danger" | "success")
 *   and it overrides the neutral accent. Leave it unset for plain reporting cards.
 */
function UsageInsights({ cards = [], loading = false, columns = 3 }) {
  if (loading) {
    return (
      <Row className="g-3 mb-3">
        {[1, 2, 3, 4].map((i) => (
          <Col md={columns} key={i}>
            <Card>
              <Card.Body>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={6} size="sm" />
                  <Placeholder xs={8} size="lg" className="mt-2" />
                </Placeholder>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  if (!cards.length) return null;

  return (
    <Row className="g-3 mb-3">
      {cards.map((card) => {
        const toneClass = card.tone ? `border-${card.tone}` : "";
        const accentStyle = card.tone ? undefined : { borderLeftColor: NEUTRAL_ACCENT };
        const iconStyle = card.tone ? undefined : { color: NEUTRAL_ACCENT };

        return (
          <Col md={columns} key={card.label}>
            <Card
              className={`h-100 border-start border-4 ${toneClass}`}
              style={accentStyle}
            >
              <Card.Body className="py-3">
                <div className="d-flex align-items-center mb-1">
                  <span
                    className={card.tone ? `text-${card.tone} me-2` : "me-2"}
                    style={iconStyle}
                  >
                    {card.icon}
                  </span>
                  <small className="text-muted fw-bold text-uppercase">{card.label}</small>
                </div>
                <h4 className="mb-1">{card.value}</h4>
                <small>
                  {card.trend !== undefined && (
                    <TrendIndicator value={card.trend} suffix={card.trendSuffix} />
                  )}
                  {card.trend !== undefined && card.caption ? " " : ""}
                  {card.caption && <span className="text-muted">{card.caption}</span>}
                </small>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}

export default UsageInsights;
